import { Component, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Dia, IControlHorarioDetalles } from 'modelos/src';

interface HorarioDia {
  dia: Dia;
  horarios: IControlHorarioDetalles[];
}

interface SegmentoHorario {
  inicio: number; // minutos desde las 00:00
  fin: number; // minutos desde las 00:00
  tipo: 'apertura' | 'cierre';
  horaInicial: number; // hora exacta del evento
  toleranciaAntes: number;
  toleranciaDespues: number;
}

@Component({
  selector: 'app-visualizador-horario',
  templateUrl: './visualizador-horario.component.html',
  styleUrl: './visualizador-horario.component.scss',
  standalone: false,
})
export class VisualizadorHorarioComponent implements OnInit {
  data = inject<{
    horarios: IControlHorarioDetalles[];
}>(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<VisualizadorHorarioComponent>>(MatDialogRef);

  public diasSemana: Dia[] = [
    'Lunes',
    'Martes',
    'Miercoles',
    'Jueves',
    'Viernes',
    'Sabado',
    'Domingo',
  ];

  public horariosPorDia = new Map<Dia, HorarioDia>();
  public horas: number[] = Array.from({ length: 24 }, (_, i) => i);
  public mediasHoras: number[] = Array.from({ length: 48 }, (_, i) => i * 0.5);
  public cuartosHora: number[] = Array.from({ length: 96 }, (_, i) => i * 0.25);

  ngOnInit(): void {
    this.procesarHorarios();
  }

  private procesarHorarios(): void {
    // Inicializar todos los días
    this.diasSemana.forEach((dia) => {
      this.horariosPorDia.set(dia, {
        dia,
        horarios: [],
      });
    });

    // Asignar horarios a cada día
    this.data.horarios?.forEach((horario) => {
      horario.dias?.forEach((dia) => {
        const diaData = this.horariosPorDia.get(dia);
        if (diaData) {
          diaData.horarios.push(horario);
        }
      });
    });
  }

  public getSegmentosPorDia(dia: Dia): SegmentoHorario[] {
    const diaData = this.horariosPorDia.get(dia);
    if (!diaData || diaData.horarios.length === 0) {
      return [];
    }

    const segmentos: SegmentoHorario[] = [];

    // Crear un segmento por cada horario configurado
    diaData.horarios.forEach((horario) => {
      const [horas, minutos] = horario.hora.split(':').map(Number);
      const horaInicial = horas * 60 + minutos;
      const toleranciaAntes = horario.toleranciaAntes || 0;
      const toleranciaDespues = horario.toleranciaDespues || 0;

      // Crear segmento que va desde (horaInicial - toleranciaAntes) hasta (horaInicial + toleranciaDespues)
      const inicio = Math.max(0, horaInicial - toleranciaAntes);
      const fin = Math.min(1440, horaInicial + toleranciaDespues);

      segmentos.push({
        inicio,
        fin,
        tipo: horario.tipo === 'Apertura' ? 'apertura' : 'cierre',
        horaInicial,
        toleranciaAntes,
        toleranciaDespues,
      });
    });

    // Ordenar segmentos por hora de inicio
    segmentos.sort((a, b) => a.inicio - b.inicio);

    return segmentos;
  }

  public calcularPosicion(minuto: number): number {
    return (minuto / 1440) * 100; // Porcentaje del día
  }

  public calcularAncho(inicio: number, fin: number): number {
    return ((fin - inicio) / 1440) * 100;
  }

  public formatearHora(minuto: number): string {
    const horas = Math.floor(minuto / 60);
    const mins = minuto % 60;
    return `${horas.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  public tieneHorarios(dia: Dia): boolean {
    const diaData = this.horariosPorDia.get(dia);
    return (diaData?.horarios.length ?? 0) > 0;
  }

  public calcularPosicionOrigen(segmento: SegmentoHorario): number {
    // Calcular la posición de horaInicial respecto al inicio del segmento
    const duracion = segmento.fin - segmento.inicio;
    const offsetDesdeInicio = segmento.horaInicial - segmento.inicio;
    return (offsetDesdeInicio / duracion) * 100;
  }

  public getNombreDiaConTilde(dia: Dia): string {
    // Mostrar con tilde solo para visualización, sin afectar las comparaciones
    if (dia === 'Miercoles') return 'Miércoles';
    if (dia === 'Sabado') return 'Sábado';
    return dia;
  }
}
